import { cloneDeep } from 'lodash'
import { computed, inject, reactive, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import useCorporation from '@/composables/useCorporation'
import useCorporationForm from '@/composables/useCorporationForm'
import useCorporationsSearch from '@/composables/useCorporationsSearch'

import useInternalSearchUser from '@/internal/modules/users/composables/useInternalSearchUser'

import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
import useCompany from '@/modules/companies/composables/useCompany'
import { Company } from '@/modules/companies/interfaces/company'

const useEditGeneralTab = (company: Company) => {
	const {
		corporation: corporationCreated,
		CorporationsCoincidences: corporations,
		setQuery: setCorporationQuery,
		searchQuery,
		confirming: confirmingCorporation,
		isCreating: isCreatingCorporation,
		handleCreate,
		handleEvaluation,
	} = useCorporationForm({ immediate: true })

	const { refetch } = useCorporationsSearch({ immediate: false })

	const { createMutation } = useCorporation()

	// const { setFiltersValue, users, searchUsers, filters } = useUsers({ immediate: true });

	const {
		data: usersInternal,
		isLoading: loadingUsersInternal,
		refetch: refetchUsers,
		setQuery: setQueryInternal,
	} = useInternalSearchUser({
		immediate: true,
		params: {
			internalOnly: 1,
		},
	})

	const toast = useToast()
	const { companies, setQuery: setCompanyQuery } = useCompaniesSearch({
		immediate: false,
		params: {
			only: ['id', 'name'],
		},
	})

	const corporationForm = reactive<{ id: number; name: string }>({
		id: 0,
		name: '',
	})

	const updateFields = reactive({
		name: company?.name,
		corporation:
			company.corporationId ?
				{
					id: company.corporationId,
					name: company.corporationName,
				}
			:	undefined,

		executive:
			company?.executiveId ?
				{
					id: company?.executiveId,
					name: company?.executiveName,
				}
			:	undefined,
		amadeusRatePlanCode: (
			Array.isArray(company?.amadeusRatePlanCode)
				? company.amadeusRatePlanCode[0]
				: company?.amadeusRatePlanCode
		)?.toUpperCase(),
		platformServiceCharge: company?.platformServiceCharge,
		platformServiceChargeType: company?.platformServiceChargeType,
		platformServiceChargePerNight: company?.platformServiceChargePerNight,
	})

	const hasChanged = ref({
		name: false,
		corporation: false,
	})

	const staticFields = ref(cloneDeep(updateFields))
	const { updateCompanyAsync } = useCompany(undefined, { immediate: false })
	const confirmingUpdate = ref(false)
	const isUpdating = ref(false)

	const { invalidateQueryData } = inject('editCompanyProvides') as {
		invalidateQueryData: () => Promise<void>
	}

	const evaluateHasChanged = () => {
		hasChanged.value.name = updateFields.name != staticFields.value.name
		hasChanged.value.corporation =
			updateFields.corporation === staticFields.value.corporation
	}

	const companyCoincidences = computed(() => {
		return companies.value.filter(
			(option) =>
				option.name.toLowerCase() !== staticFields.value.name.toLowerCase()
		)
	})

	const anyDataHasChanged = computed(() => {
		return hasChanged.value.name || hasChanged.value.corporation
	})

	const handleUpdateEvaluation = async () => {
		isUpdating.value = true
		if (hasChanged.value.name && companyCoincidences.value.length) {
			confirmingUpdate.value = true
		} else {
			await handleUpdate()
			isUpdating.value = false
		}
	}

	const handleUpdate = async () => {
		isUpdating.value = true
		try {
			await updateCompanyAsync({
				companyId: company?.id!,
				name: updateFields.name,
				corporationId: updateFields.corporation?.id,
				executiveId: updateFields.executive?.id,
				amadeusRatePlanCode: updateFields.amadeusRatePlanCode,
				platformServiceCharge: updateFields.platformServiceCharge,
				platformServiceChargeType: updateFields.platformServiceChargeType,
				platformServiceChargePerNight:
					updateFields.platformServiceChargePerNight,
			})

			staticFields.value = {
				name: updateFields.name,
				corporation: updateFields.corporation,
				executive: updateFields.executive,
				amadeusRatePlanCode: updateFields.amadeusRatePlanCode,
				platformServiceCharge: updateFields.platformServiceCharge,
				platformServiceChargeType: updateFields.platformServiceChargeType,
				platformServiceChargePerNight:
					updateFields.platformServiceChargePerNight,
			}
		} catch (err) {
			console.log(err)
		} finally {
			isUpdating.value = false
			confirmingUpdate.value = false
			evaluateHasChanged()
			invalidateQueryData()
		}
	}

	const onCancelUpdate = () => {
		updateFields.name = company?.name
		isUpdating.value = false
	}

	const handleCreateCorporation = async () => {
		if (!corporationForm.name) {
			toast.error('El nombre del corporativo es requerido.')
			return
		}

		try {
			const duplicatedCorporation = corporations.value.filter(
				(corporation) =>
					corporation.name.toLowerCase() === corporationForm.name.toLowerCase()
			)

			if (!duplicatedCorporation.length) {
				if (corporations.value.length) {
					confirmingCorporation.value = true

					return
				} else {
					await handleCreateCorpo()
				}
			} else {
				toast.error(
					`Ya existe un grupo con el nombre "${corporationForm.name}".`
				)
				return
			}
		} catch (err) {
			console.log(err)
		}
	}

	const handleCreateCorpo = async () => {
		const currentName = corporationForm.name
		const data = await createMutation.mutateAsync(currentName)

		await refetch()

		confirmingCorporation.value = false

		if (data) {
			updateFields.corporation = {
				id: data.id,
				name: data.name,
			}

			await handleUpdate()
		}
	}

	watch(
		updateFields,
		() => {
			evaluateHasChanged()
		},
		{ deep: true }
	)

	return {
		// users,
		corporationForm,
		updateFields,
		staticFields,
		hasChanged,
		confirmingUpdate,
		isUpdating,
		companyCoincidences,
		anyDataHasChanged,
		corporations,
		searchQuery,
		confirmingCorporation,
		isCreatingCorporation,
		handleUpdateEvaluation,
		handleUpdate,
		onCancelUpdate,
		setCorporationQuery,
		handleCreateCorporation,
		handleEvaluation,
		setCompanyQuery,
		handleCreateCorpo,
		usersInternal,
		setQueryInternal,
		loadingUsersInternal,
	}
}

export default useEditGeneralTab
